import flatten, { unflatten } from 'flat'
import moment from 'moment'
import _ from 'lodash'

/**
 * This reducer is in charge of the audit history table. It's primary duty is to take
 * a delta from the BE and transform it such that we can show a field level diff to the user.
 * Any change of field is shown on it's own expcept those represented as arrays, which are shown as
 * JSON to the user.
 *
 * We do the steps necessary to transform as follows:
 *  1. Fetch a list of deltas from the BE
 *  2. Add the deltas so we can get before and after states for each change
 *  3. Flatten the changes
 *  4. Iterate through every field in each change and compare
 */

const initialTableState = {
  content: [],
  total_elements: 0,
  isLoading: true,
}
const DO_NOT_SHOW_FIELDS = [
  'update_date',
  'created_by',
  'created_date',
  'updated_by',
]

export const auditTableReducer =
  tableType =>
  (state = initialTableState, action) => {
    switch (action.type) {
      case `GET_${tableType}`:
        /* eslint-disable no-case-declarations */
        const chronologicalHistory = _.cloneDeep(action.history).sort((a, b) =>
          moment(a.updated_date).diff(moment(b.updated_date))
        )
        const changeLevelHistory = createAuditHistory(chronologicalHistory)
        const fieldLevelHistory = createFieldLevelDiffs(changeLevelHistory)
        /* eslint-enable no-case-declarations */

        // reverse so user sees history in reverse chronological order
        _.reverse(fieldLevelHistory)

        return {
          ...state,
          content: fieldLevelHistory,
          total_elements: fieldLevelHistory.length,
          isLoading: false,
        }
      default:
        return state
    }
  }

/**
 * Function to remove specified fields from the history
 * @param {array} completeHistory - The full history array containing deltas and dates
 *
 * @returns {array} - an array of objects that have from and to values that show chagnes
 */
export const createAuditHistory = completeHistory => {
  completeHistory.reduce((prevState, changes) => {
    /**
     * we unflatten here because the BE library that does the deltas sends a different format
     * for insertions vs. modification. For example, an insertion will look like
     *  "\/venue_configuration": {
     *     "venue_classification": "TRUSTEE_STANDARD",
     *     "venue_type": "ONLINE"
     *   }
     *
     * while a modification will look like:
     * "\/venue_configuration\/venue_type": "LIVE",
     * We want to make the same type equal so we can make our to and from values
     *
     * We get the item at the 0 index of unflatten beause the api sends us data with a "/"
     * at the front of it so flatten thinks it is in an aray.
     */
    const modifications =
      unflatten(changes.delta.modified, {
        delimiter: '/',
        overwrite: true,
      })[0] || {}
    const additions =
      unflatten(changes.delta.added, { delimiter: '/', overwrite: true })[0] ||
      {}

    // We first merge the modifications into our previous state
    const merged = _.merge({}, prevState, modifications, additions)

    // Then, remove the deleted things from the state
    const changedState = remove(merged, changes.delta.removed || [])

    changes.to = changedState
    changes.from = _.cloneDeep(prevState)
    return changedState
  }, {})
  return completeHistory
}

/**
 * Function to remove specified fields from the history
 * @param {object} historyObject - Object containing items to remove
 * @param {array} removed - The array of removed items
 *
 * @returns {object} - Cleaned history object
 */
export const remove = (historyObject, removed) => {
  const cleanedRemoved = removed.map(item => {
    // We put the item in an object here so flatten knows what to do with it
    // We choose to flatten and unflatten here to convert the object path from
    // "/topField/1/nestedField/fieldName" to "topField[0].nestedField.fieldName"
    const formatforFlatten = { [item]: null }
    const formattedItem = flatten(
      unflatten(formatforFlatten, { delimiter: '/' })[0]
    )
    return Object.keys(formattedItem)[0]
  })

  const clonedHistory = _.cloneDeep(historyObject)
  cleanedRemoved.forEach(removedItem => {
    /**
     * Basic rundown of what we are doing here:
     *  1. Split removed path
     *  2. check if last item is a number and if that path is an array
     *  3. Treat as array and remove, otherwise treat as object and unset
     */
    const splitRemoved = removedItem.split('.')
    const lastSplit = _.toNumber(_.last(splitRemoved))
    const shortenedPath = _.dropRight(splitRemoved, 1).join('.')
    const isRemovingArrayItem =
      _.isNumber(lastSplit) && _.isArray(_.get(clonedHistory, shortenedPath))
    if (isRemovingArrayItem) {
      const clonedArray = _.cloneDeep(_.get(clonedHistory, shortenedPath))
      _.remove(clonedArray, (item, index) => index === lastSplit)
      _.set(clonedHistory, shortenedPath, clonedArray)
    } else {
      _.unset(clonedHistory, removedItem)
    }
  })
  return clonedHistory
}

// Sometimes BE will send uninitialized fields as undefined for the form portion
// When compared with a default initial value (such as [] or ""), the audit table will incorrectly log a diff
// Adding an extra check for initialized fields to log only valid diffs
const isInvalidInitialDiff = (from, to) =>
  from === undefined && ((Array.isArray(to) && !to.length) || to === '')

const isEqual = (from, to) => {
  let fromVal = from
  let toVal = to

  if (Array.isArray(from) && Array.isArray(to)) {
    fromVal = from.sort()
    toVal = to.sort()
  }

  return _.isEqual(fromVal, toVal)
}

/**
 * Function to turn a change level diff to field level diffs
 * @param {object} historyObject - Object containing changes
 *
 * @returns {array} - an array of objects of field level changes
 */
export const itemDiff = historyObject => {
  const flattenedFrom = flatten(historyObject.from, {
    overwrite: true,
    safe: true,
  })
  const flattenedTo = flatten(historyObject.to, {
    overwrite: true,
    safe: true,
  })

  // We want to compare all unique fields to see what's changed
  const fromKeys = _.keys(flattenedFrom)
  const toKeys = _.keys(flattenedTo)
  const uniqKeys = _.uniq([...toKeys, ...fromKeys])

  return _.map(uniqKeys, flattenedFieldName => {
    // Skip if field values haven't changed
    if (
      isEqual(
        flattenedTo[flattenedFieldName],
        flattenedFrom[flattenedFieldName]
      ) ||
      isInvalidInitialDiff(
        flattenedFrom[flattenedFieldName],
        flattenedTo[flattenedFieldName]
      )
    )
      return

    // Skip the fields we don't want to show
    if (_.some(DO_NOT_SHOW_FIELDS, el => _.includes(flattenedFieldName, el)))
      return

    return {
      field: _.last(flattenedFieldName.split('.')),
      to: flattenedTo[flattenedFieldName],
      from: flattenedFrom[flattenedFieldName],
      updated_date: historyObject.updated_date,
      updated_by: historyObject.updated_by,
    }
  })
}

// Cleans our results and stamps _revisionIndex for row grouping
const createFieldLevelDiffs = dataRows => {
  const withRevisionIndex = dataRows.map((item, revisionIndex) => {
    const fields = itemDiff(item)
    return fields.map(field =>
      field ? { ...field, _revisionIndex: revisionIndex } : field
    )
  })
  return _.compact(_.flatten(withRevisionIndex))
}
